import type { AnalyticsMetrics } from '@/types';
import { formatPercentage } from '@/utils/formatting';

interface KPICardsProps {
  metrics: AnalyticsMetrics;
}

const KPICards = ({ metrics }: KPICardsProps) => {
  const csatTarget = 70;
  const isCSATAboveTarget = metrics.csatPercentage >= csatTarget;

  const cards = [
    {
      title: 'Total Responses',
      value: metrics.totalResponses,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      bgColor: 'bg-gradient-to-br from-blue-500 to-blue-600',
      textColor: 'text-blue-600',
    },
    {
      title: 'CSAT Score',
      value: formatPercentage(metrics.csatPercentage),
      subtitle: `Target: ${csatTarget}%`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bgColor: isCSATAboveTarget
        ? 'bg-gradient-to-br from-green-500 to-green-600'
        : 'bg-gradient-to-br from-red-500 to-red-600',
      textColor: isCSATAboveTarget ? 'text-green-600' : 'text-red-600',
      warning: !isCSATAboveTarget
        ? `Below target by ${formatPercentage(csatTarget - metrics.csatPercentage)}`
        : undefined,
    },
    {
      title: 'Would Recommend',
      value: formatPercentage(metrics.recommendationPercentage),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      bgColor: 'bg-gradient-to-br from-purple-500 to-purple-600',
      textColor: 'text-purple-600',
    },
    {
      title: 'Overall Rating',
      value: `${metrics.categoryAverages.overall.toFixed(1)}/5`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      bgColor: 'bg-gradient-to-br from-primary-500 to-primary-600',
      textColor: 'text-primary-600',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
      {cards.map((card, index) => (
        <div
          key={index}
          className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className={`${card.bgColor} p-3 rounded-lg text-white`}>
                {card.icon}
              </div>
              {card.warning && (
                <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                  Alert
                </span>
              )}
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{card.value}</p>
              <p className="text-sm font-semibold text-gray-700">{card.title}</p>
              {card.subtitle && (
                <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
              )}
              {card.warning && (
                <p className="text-xs text-red-600 mt-2 font-medium">{card.warning}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default KPICards;

